import React, { useState, useEffect } from "react";
import { useNavigate } from "react-router-dom";
import { toast } from "react-toastify";
import Navbar from "../components/Sidebar/Sidebar";
import axios from "axios";

const ProfilePage = () => {
  const [user, setUser] = useState({}); // State to store user profile data
  const [fieldToEdit, setFieldToEdit] = useState(""); // State to store the field to edit
  const [showModal, setShowModal] = useState(false); // State to control modal visibility
  const [editValue, setEditValue] = useState(""); // State to store the value being edited
  const [loading, setLoading] = useState(false); // State to indicate loading status
  const navigate = useNavigate(); // useNavigate hook for navigation
  
  // Function to validate the token and check if the session is valid
  const fetchDataValid = async () => {
    const token = localStorage.getItem("token"); // Retrieve token from localStorage
  
    if (!token) { // If no token is found, navigate to the login page
      navigate("/login");
      return;
    }
  
    try {
      const response = await axios.post(
        `${process.env.REACT_APP_SERVER}/api/auth/validateToken`, // Make a request to validate token
        {},
        {
          headers: {
            Authorization: `Bearer ${token}`, // Send the token in the header
          },
        }
      );
      if (response.data.isValid) { // If the token is valid, do nothing
        return;
      } else {
        toast.error("Session expired. Please log in again."); // Show error if session is expired
        navigate("/login"); // Navigate to login page
      }
    } catch (error) {
      console.error("Error during token validation:", error); // Log error if the request fails
      toast.error("Unable to validate session. Please log in again."); // Show error message
      navigate("/login"); // Navigate to login page
    }
  };
  
  useEffect(() => {
    fetchDataValid(); // Call fetchDataValid on component mount to validate session
  }, []); // Empty dependency array ensures it runs only once on mount
  
  // Fetch user profile data after session validation
  useEffect(() => {
    axios
      .get(`${process.env.REACT_APP_SERVER}/api/auth/profile`, { // Make a GET request to fetch profile
        headers: {
          Authorization: `Bearer ${localStorage.getItem("token")}`, // Send token in the header
        },
      })
      .then((response) => {
        setUser(response.data); // Set the user data to the state
      })
      .catch((error) => {
        console.error("Error fetching profile:", error); // Log error if fetching profile fails
        toast.error("Failed to load profile data."); // Show error message
      });
  }, []); // Empty dependency array ensures it runs only once on mount
  
  // Function to handle click on edit button for a field
  const handleEditClick = (field) => {
    if (field === "email") return; // Prevent editing email field
    setFieldToEdit(field); // Set the field to edit
    setEditValue(user[field] || ""); // Set the current value of the field to be edited
    setShowModal(true); // Show the modal to edit the field
  };
  
  // Function to save the edited value
  const handleSave = () => {
    if (!editValue.trim()) { // Check if the edited value is empty
      toast.warn("Value cannot be empty."); // Show warning if value is empty
      return;
    }
  
    setLoading(true); // Set loading state to true while saving the changes
    axios
      .put(
        `${process.env.REACT_APP_SERVER}/api/auth/updateProfile`, // Make a PUT request to update profile
        { field: fieldToEdit, value: editValue }, // Send the field and new value in the request body
        {
          headers: {
            Authorization: `Bearer ${localStorage.getItem("token")}`, // Send token in the header
          },
        }
      )
      .then(() => {
        setUser((prevUser) => ({ ...prevUser, [fieldToEdit]: editValue })); // Update user data in state
        setShowModal(false); // Close the modal
        toast.success( // Show success message after update
          `${
            fieldToEdit.charAt(0).toUpperCase() + fieldToEdit.slice(1)
          } updated successfully.`
        );
      })
      .catch((error) => {
        console.error("Error updating profile:", error); // Log error if update fails
        toast.error(`Failed to update ${fieldToEdit}.`); // Show error message
      })
      .finally(() => setLoading(false)); // Set loading state to false after the request completes
  };
  
  // Function to navigate to the dashboard
  const goToDashboard = () => {
    navigate("/dashboard"); // Navigate to the dashboard page
  };
  
  return (
    <>
      <Navbar />
      <div className="p-4 sm:ml-64">
        <div className="p-4 border-gray-200 rounded-lg dark:border-gray-700 mt-14">
          <div className="container mx-auto mt-10 max-w-2xl">
            <div className="flex items-center mb-5">
              <div className="w-16 h-16 bg-gray-200 rounded-full flex items-center justify-center">
                <span className="text-3xl text-gray-600">
                  {user.name ? user.name[0] : "U"}
                </span>
              </div>
              <div className="ml-4">
                <h3 className="text-2xl font-semibold">{user.name}</h3>
                <p className="text-gray-600">{user.email}</p>
              </div>
            </div>

            <div className="space-y-5">
              {["name", "password", "phone"].map((field) => (
                <div
                  key={field}
                  className="flex items-center justify-between bg-gray-100 p-4 rounded-md shadow"
                >
                  <div>
                    <strong className="capitalize">{field}:</strong>{" "}
                    {field === "password" ? "*******" : user[field]}
                  </div>

                  <button
                    className={`text-blue-500 ${
                      field === "email" ? "cursor-not-allowed" : ""
                    }`}
                    onClick={() => handleEditClick(field)}
                  >
                    ✎
                  </button>
                </div>
              ))}
            </div>

            {/* Modal */}
            {showModal && (
              <div className="fixed inset-0 z-40 flex items-center justify-center bg-gray-800 bg-opacity-50">
                <div className="bg-white w-full max-w-lg p-6 rounded-lg shadow-lg mt-[4rem]">
                  <h4 className="text-lg font-medium mb-4">
                    Edit {fieldToEdit}
                  </h4>
                  <input
                    type={fieldToEdit === "password" ? "password" : "text"}
                    value={editValue}
                    onChange={(e) => setEditValue(e.target.value)}
                    className="w-full px-4 py-2 border rounded-md focus:outline-none focus:ring focus:ring-blue-300"
                  />
                  <div className="mt-6 flex justify-end space-x-3">
                    <button
                      className="px-4 py-2 bg-black text-white rounded-md mr-2"
                      onClick={() => setShowModal(false)}
                    >
                      Cancel
                    </button>
                    <button
                      className="px-4 py-2 text-black rounded-md bg-[#B1D4E0]-100 dark:bg-[#B1D4E0] "
                      onClick={handleSave}
                      disabled={loading}
                    >
                      {loading ? "Saving..." : "Save"}
                    </button>
                  </div>
                </div>
              </div>
            )}

            {/* Go to Dashboard Button */}
            <div className="mt-6 flex justify-end">
              <button
                onClick={goToDashboard}
                className="px-4 py-2 text-black rounded-md bg-[#B1D4E0]-100 dark:bg-[#B1D4E0] "
              >
                Go to Dashboard
              </button>
            </div>
          </div>
        </div>
      </div>
    </>
  );
};

export default ProfilePage;
